/**
 * tutorPreCallAdapter — la séance que « Rejoindre » ouvre, telle que le précall du masteur la montre.
 *
 * QUELLE séance : celle de la route (`liveSessionId`), dans `currentUser.live_sessions` — ce que lisait
 * `PreCallScreen` (`findEntryLiveSession`, la même lecture que l'élève), sur sa prochaine occurrence non
 * finalisée (`nextLiveFromToday`) pour l'heure.
 *
 * QUI est déjà là : la règle de l'élève (`connectedStudents`) — les élèves de la séance que la salle voit
 * connectés, jamais un tuteur, l'ancien `AvatarGroupM` de `PreCallScreen.js:346-348`. Le masteur connaît ses
 * élèves : il les voit par leur visage ET leur prénom (l'élève, lui, ne voit que des visages).
 */
import moment from "moment"

import { nextLiveFromToday } from "../../../../../data/utility/liveDay"
import { type UINamedStackPerson } from "../../../ui/types/studentLive"
import {
    connectedStudents,
    findEntryLiveSession,
    toStackPerson,
    type ApiEntryAccount,
} from "../../student/LiveEntry/liveEntryAdapter"

export type TutorPreCallModel = {
    /** La séance est dans le compte : le test guidé s'ouvre sans attendre. */
    found: boolean
    /** Le nom de la salle (« LIVE - {salle} » de l'ancien en-tête) ; vide sans séance. */
    roomName: string
    /** L'heure de la prochaine occurrence (« YYYY-MM-DD HH:mm »), `null` sans séance ou sans occurrence. */
    date: string | null
    /** La durée de la séance, en minutes. */
    duration: number
    /** Les élèves déjà connectés, dans l'ordre de la séance. */
    present: UINamedStackPerson[]
}

export function toTutorPreCall(args: {
    account: ApiEntryAccount | null | undefined
    liveSessionId: number | string
    /** Les identités que la salle voit connectées. */
    liveParticipants: (number | string)[] | null | undefined
    now?: moment.Moment
}): TutorPreCallModel {
    const { account, liveSessionId, liveParticipants, now = moment() } = args
    const liveSession = findEntryLiveSession(account, liveSessionId)
    if (liveSession == null) return { found: false, roomName: "", date: null, duration: 0, present: [] }

    const occurrence = nextLiveFromToday([liveSession], now) as { date?: string } | undefined
    // Le masteur n'est jamais parmi les élèves (`is_tutor`) : personne à exclure de plus.
    const present = connectedStudents(liveSession.students, liveParticipants).map((student) => ({
        ...toStackPerson(student),
        name: student.firstname?.trim() ?? "",
    }))

    return {
        found: true,
        roomName: liveSession.live_room?.name?.trim() ?? "",
        date: occurrence?.date ?? null,
        duration: liveSession.duration ?? 0,
        present,
    }
}
